import React from 'react';
import { Link } from 'react-router-dom';
import { chainName, isTestnetMode, testnetFaucetUrl } from '../../constant/constant';
import { cronpayLogoSrc } from '../../config/brandAssets';
import { DOCS_PATH, JOBS_PORTAL_PATH } from '../../config/paths';
import { useTheme } from '../../context/ThemeContext';
import { LANDING_NAV_LINKS, LANDING_SOCIAL_X } from './landingConfig';

const footerLinkClass =
    'text-sm text-zinc-600 transition-colors hover:text-teal-700 dark:text-zinc-400 dark:hover:text-teal-400';

const FooterColumn = ({ title, children }) => (
    <div>
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-500">
            {title}
        </p>
        <div className="flex flex-col gap-2.5">{children}</div>
    </div>
);

const SocialButton = ({ href, label, icon, external, disabled, disabledLabel }) => {
    if (disabled) {
        return (
            <span
                className="inline-flex cursor-not-allowed items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-sm text-zinc-400 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-600"
                title={disabledLabel}
            >
                <i className={`${icon} w-4 text-center text-base`} aria-hidden />
                <span className="font-medium">{label}</span>
                <span className="ml-auto rounded-md bg-zinc-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500">
                    Soon
                </span>
            </span>
        );
    }

    const className =
        'inline-flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm font-medium text-zinc-700 shadow-sm transition hover:border-teal-300 hover:bg-teal-50/50 hover:text-teal-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-teal-800 dark:hover:bg-teal-950/30 dark:hover:text-teal-300';

    if (external) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
                <i className={`${icon} w-4 text-center text-base`} aria-hidden />
                <span>{label}</span>
                <i className="fa-solid fa-arrow-up-right-from-square ml-auto text-[10px] opacity-50" aria-hidden />
            </a>
        );
    }

    return (
        <Link to={href} className={className}>
            <i className={`${icon} w-4 text-center text-base`} aria-hidden />
            <span>{label}</span>
        </Link>
    );
};

const LandingFooter = ({ onLaunch, onOpenJobs }) => {
    const { resolvedTheme } = useTheme();
    const logoSrc = cronpayLogoSrc(resolvedTheme);
    const networkLabel = chainName || 'Arc';
    const year = new Date().getFullYear();

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <footer className="relative border-t border-zinc-200 bg-zinc-50/90 dark:border-zinc-800/80 dark:bg-zinc-950">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />

            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
                    <div className="sm:col-span-2 lg:col-span-5">
                        <Link to="/" className="inline-flex items-center">
                            <img
                                src={logoSrc}
                                alt="CronPay"
                                className="block h-10 w-auto object-contain sm:h-11"
                            />
                        </Link>
                        <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                            USDC-native payments, tracked escrow, and team task payouts on {networkLabel}.
                            Non-custodial: your wallet signs every transfer.
                        </p>
                        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-teal-200/80 bg-teal-50/80 px-3 py-1.5 text-xs font-medium text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-300">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500 opacity-60" />
                                <span className="relative h-2 w-2 rounded-full bg-teal-500" />
                            </span>
                            Live on {networkLabel}
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <FooterColumn title="Product">
                            <button type="button" onClick={onLaunch} className={`${footerLinkClass} text-left`}>
                                Payment dashboard
                            </button>
                            <button type="button" onClick={onOpenJobs} className={`${footerLinkClass} text-left`}>
                                Company &amp; Job
                            </button>
                            {LANDING_NAV_LINKS.filter(
                                (item) => item.id !== 'try-testnet' || isTestnetMode,
                            ).map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => scrollTo(item.id)}
                                    className={`${footerLinkClass} text-left`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </FooterColumn>
                    </div>

                    <div className="lg:col-span-2">
                        <FooterColumn title="Resources">
                            <Link to={`${DOCS_PATH}/welcome`} className={footerLinkClass}>
                                Documentation
                            </Link>
                            <Link to={`${DOCS_PATH}/quick-start`} className={footerLinkClass}>
                                Quick start
                            </Link>
                            <Link to={JOBS_PORTAL_PATH} className={footerLinkClass}>
                                Jobs portal
                            </Link>
                            {isTestnetMode ? (
                                <a
                                    href={testnetFaucetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 transition hover:text-teal-600 dark:text-teal-400 dark:hover:text-teal-300"
                                >
                                    <i className="fa-solid fa-droplet text-xs" aria-hidden />
                                    Testnet faucet
                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-60" aria-hidden />
                                </a>
                            ) : null}
                        </FooterColumn>
                    </div>

                    <div className="sm:col-span-2 lg:col-span-3">
                        <FooterColumn title="Community">
                            <SocialButton
                                href={LANDING_SOCIAL_X}
                                label="Follow on X"
                                icon="fa-brands fa-x-twitter"
                                external
                            />
                            <SocialButton
                                label="Discord"
                                icon="fa-brands fa-discord"
                                disabled
                                disabledLabel="Discord community coming soon"
                            />
                            {isTestnetMode ? (
                                <a
                                    href={testnetFaucetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1 flex items-center gap-3 rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-4 transition hover:border-teal-300 hover:shadow-md dark:border-teal-900/60 dark:from-teal-950/50 dark:to-zinc-900 dark:hover:border-teal-700"
                                >
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
                                        <i className="fa-solid fa-droplet" aria-hidden />
                                    </span>
                                    <span className="min-w-0 text-left">
                                        <span className="block text-sm font-semibold text-zinc-900 dark:text-white">
                                            Get testnet USDC
                                        </span>
                                        <span className="block text-xs text-zinc-500 dark:text-zinc-500">
                                            Circle faucet for {networkLabel}
                                        </span>
                                    </span>
                                    <i className="fa-solid fa-arrow-up-right-from-square shrink-0 text-xs text-teal-600 dark:text-teal-400" aria-hidden />
                                </a>
                            ) : null}
                        </FooterColumn>
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-zinc-200/80 pt-8 dark:border-zinc-800/80 sm:flex-row">
                    <p className="text-center text-xs text-zinc-500 dark:text-zinc-500 sm:text-left">
                        &copy; {year} CronPay. All rights reserved.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-zinc-500 dark:text-zinc-500">
                        <Link to={`${DOCS_PATH}/trust-and-safety`} className="transition hover:text-teal-700 dark:hover:text-teal-400">
                            Trust &amp; safety
                        </Link>
                        <span className="hidden h-3 w-px bg-zinc-300 dark:bg-zinc-700 sm:inline" aria-hidden />
                        <span>{networkLabel}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default LandingFooter;
